import { checkAccessToPath, isAdmin } from 'frontend/common/access-helpers'
import { computed, type ComputedRef, type InjectionKey, provide, type Ref } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

export const canSymbol: InjectionKey<CanType> = Symbol('can')
export const canSeePathSymbol: InjectionKey<CanSeePathType> = Symbol('canSeePath')
export const isAdminSymbol: InjectionKey<Ref<boolean>> = Symbol('isAdmin')

export type CanType<T = boolean> = (actionOrPath: string, dataModelType?: string) => T
export type CanSeePathType<T = boolean> = (path: string) => T

export interface IUseEventAccess {
  currentEou: ComputedRef
  can: CanType
  canSeePath: CanSeePathType
}

export function useEventAccess(): IUseEventAccess {
  const route = useRoute()
  const store = useStore()

  const currentEou = computed(() => {
    const user = store.state.session.user
    if (user?.impersonatedCurrentOrganizationUser) {
      return (user?.impersonatedCurrentOrganizationUser?.activeEventOrganizationUsers || []).find(
        eou => eou.event?.slug == route.params.eventSlug,
      )
    } else {
      return (user?.currentOrganizationUser?.activeEventOrganizationUsers || []).find(
        eou => eou.event?.slug == route.params.eventSlug,
      )
    }
    // return (user?.currentOrganizationUser?.activeEventOrganizationUsers || []).find(
    //   eou => eou.event?.slug == route.params.eventSlug,
    // )
  })

  const computedIsAdmin = computed(() => {
    return isAdmin(currentEou.value)
  })

  const canSeePath: CanSeePathType = path => {
    return checkAccessToPath(path, currentEou)
  }

  const can: CanType = (actionOrPath, dataModelType = null) => {
    if (computedIsAdmin.value) {
      return true
    } else if (!dataModelType) {
      return checkAccessToPath(currentEou.value, actionOrPath)
    } else {
      return !![
        ...(currentEou.value?.role?.roleScopes || []),
        ...(currentEou.value?.additionalRoleScopes || []),
      ]?.find(
        el => el.dataModelType == dataModelType && (el.actionTypes || []).includes(actionOrPath),
      )
    }
  }

  // TODO(bartek): remove string provides after full migration to symbols
  provide(isAdminSymbol, computedIsAdmin)
  provide('isAdmin', computedIsAdmin)
  provide(canSeePathSymbol, canSeePath)
  provide('canSeePath', canSeePath)
  provide(canSymbol, can)
  provide('can', can)

  return {
    currentEou,
    can,
    canSeePath,
  }
}
